import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const KITCHEN_IMG = "/images/kitchen-interior.webp";
const ATLANTA_IMG = "/images/atlanta-site.webp";
const DUSK_IMG = "/images/kitchen-dusk-lot.webp";
const BLACK_CUTOUT_IMG = "/images/unit-black-cutout.webp";
const SITE_IMG = "/images/site-lot-sunset.webp";
const DEPLOY_IMG = "/images/deploy-crane.webp";
const BUILD_IMG = "/images/build-fabrication.webp";
const MATCH_IMG = "/images/match-wheel.webp";
const OPEN_IMG = "/images/open-famcourt.webp";

const HOW_IT_WORKS_STEPS = [
  { word: "SITE", img: SITE_IMG, text: "A commercial location with underused space." },
  { word: "MATCH", img: MATCH_IMG, contain: true, text: "A restaurant operator ready to open selects a site." },
  { word: "BUILD", img: BUILD_IMG, text: "The kitchen is fabricated to one exact standard." },
  { word: "DEPLOY", img: DEPLOY_IMG, text: "A 40-foot kitchen shell is transported and set on-site." },
  { word: "OPEN", img: OPEN_IMG, text: "Lights on. Windows up. Customers served." },
];


const trackCTA = (label) => { if (window.gtag) window.gtag("event", "cta_click", { event_category: "engagement", event_label: label }); };

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };

function ParallaxImage({ src, alt, speed = 0.15, className = "", style = {} }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [speed * -100, speed * 100]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`} style={style}>
      <motion.img src={src} alt={alt} style={{ y }} className="w-full h-full object-cover scale-110" loading="lazy" />
    </div>
  );
}

export default function HowItWorksPage() {
  return (
    <div data-testid="how-it-works-page" style={{ backgroundColor: "#2d2d2d" }}>
      {/* Page intro */}
      <section data-testid="hiw-intro" className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pt-24 md:pt-28 pb-10 md:pb-12">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: "#d4782f" }}>
            The PadSite Playbook
          </motion.p>
          <motion.h1
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[1.05]"
            style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#FFFFFF" }}
          >
            HOW IT <span style={{ color: "#d4782f" }}>WORKS</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="text-base md:text-lg max-w-xl mt-6" style={{ color: "rgba(255,255,255,0.65)" }}>
            Five moves from underused pad to open restaurant. Scroll through the sequence.
          </motion.p>
        </motion.div>
      </section>

      {/* Steps scroll sequence */}
      <section id="how-it-works" data-testid="how-it-works" style={{ backgroundColor: "#2d2d2d" }}>
        {HOW_IT_WORKS_STEPS.map((step) => (
          <div key={step.word} className={`relative ${step.tall ? "min-h-[100vh]" : "min-h-[85vh]"} flex items-end overflow-hidden`} data-testid={`step-${step.word.toLowerCase()}`}>
            {step.contain ? (
              <div className="absolute inset-0" style={{ backgroundColor: "#2d2d2d" }}>
                <img
                  src={SITE_IMG}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover blur-sm scale-105 opacity-90"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <img
                    src={step.img}
                    alt="PadSite Partners restaurant concept wheel"
                    className="relative object-contain max-h-[52vh] md:max-h-[58vh] max-w-[92vw] md:max-w-[58vw]"
                    loading="lazy"
                    data-testid="match-wheel-static"
                  />
                </div>
              </div>
            ) : step.img ? (
              <ParallaxImage src={step.img} alt="" speed={0.12} className="absolute inset-0" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 60%, rgba(212,120,47,0.18) 0%, rgba(45,45,45,0) 70%)" }}>
                <motion.img
                  src={BLACK_CUTOUT_IMG}
                  alt="The 40-foot PadSite kitchen ready for deployment"
                  className="w-full max-w-4xl px-6"
                  style={{ filter: "drop-shadow(0 50px 70px rgba(0,0,0,0.55))" }}
                  initial={{ opacity: 0, x: 80 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-20%" }}
                  transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
                  loading="lazy"
                />
              </div>
            )}
            <div className="absolute inset-0 step-scrim" />
            <div className="relative w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pb-16 md:pb-20">
              <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-25%" }} transition={{ duration: 0.7 }}>
                <h3 className="font-bold tracking-tighter leading-none mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(3rem, 10vw, 8rem)", color: "#FFFFFF" }}>
                  {step.word}
                </h3>
                <p className="text-base md:text-lg max-w-md" style={{ color: "rgba(255,255,255,0.8)" }}>{step.text}</p>
              </motion.div>
            </div>
          </div>
        ))}
        <div className="py-10 md:py-14 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-xs md:text-sm uppercase tracking-[0.35em] font-bold"
            style={{ color: "rgba(255,255,255,0.55)", wordSpacing: "0.7em" }}
          >
            Develop Deploy Grow
          </motion.p>
        </div>
      </section>

      {/* Choose your path */}
      <section data-testid="choose-path" style={{ backgroundColor: "#2d2d2d" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-5xl font-bold tracking-tighter mb-12 text-center"
            style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#FFFFFF" }}
          >
            WHAT CAN PADSITE <span style={{ color: "#d4782f" }}>DO FOR YOU?</span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/sites" onClick={() => trackCTA("path_open_restaurant")} data-testid="path-restaurant" className="group relative block overflow-hidden min-h-[60vh]">
              <img src={KITCHEN_IMG} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 transition-opacity duration-500" style={{ background: "linear-gradient(180deg, rgba(45,45,45,0.2) 0%, rgba(45,45,45,0.88) 100%)" }} />
              <div className="relative h-full flex flex-col justify-end p-8 md:p-12">
                <h3 className="text-2xl md:text-4xl font-bold tracking-tighter mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#FFFFFF" }}>
                  I WANT TO OPEN<br />A RESTAURANT
                </h3>
                <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em]" style={{ color: "#d4782f" }}>
                  Find your kitchen <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </p>
              </div>
            </Link>
            <Link to="/landlords" onClick={() => trackCTA("path_have_property")} data-testid="path-property" className="group relative block overflow-hidden min-h-[60vh]">
              <img src={ATLANTA_IMG} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 transition-opacity duration-500" style={{ background: "linear-gradient(180deg, rgba(45,45,45,0.2) 0%, rgba(45,45,45,0.88) 100%)" }} />
              <div className="relative h-full flex flex-col justify-end p-8 md:p-12">
                <h3 className="text-2xl md:text-4xl font-bold tracking-tighter mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#FFFFFF" }}>
                  I HAVE COMMERCIAL<br />PROPERTY
                </h3>
                <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em]" style={{ color: "#d4782f" }}>
                  Activate your property <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final cinematic CTA */}
      <section data-testid="final-cta" className="relative overflow-hidden min-h-[90vh] flex items-center justify-center" style={{ backgroundColor: "#2d2d2d" }}>
        <ParallaxImage src={DUSK_IMG} alt="A PadSite kitchen operating at dusk in a metro Atlanta parking lot" speed={0.1} className="absolute inset-0" />
        <div className="absolute inset-0" style={{ background: "rgba(45,45,45,0.55)" }} />
        <div className="relative text-center px-6 py-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-bold tracking-tighter leading-[1.02] mb-10" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span className="block text-5xl sm:text-7xl lg:text-8xl" style={{ color: "#FFFFFF" }}>DEVELOP</span>
              <span className="block text-5xl sm:text-7xl lg:text-8xl" style={{ color: "#d4782f" }}>DEPLOY</span>
              <span className="block text-5xl sm:text-7xl lg:text-8xl" style={{ color: "#FFFFFF" }}>GROW</span>
            </motion.h2>
            <motion.div variants={fadeUp}>
              <Link
                to="/contact"
                onClick={() => trackCTA("final_get_started")}
                data-testid="final-get-started"
                className="inline-flex items-center gap-3 px-10 py-5 font-bold tracking-wide text-sm md:text-base transition-all glow-btn rounded-none"
                style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
              >
                Get Started <ArrowRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
